import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { translations, TranslationData } from '../locales/helpdesk/translations';

interface LanguageContextType {
  lang: 'en' | 'ar';
  dir: 'ltr' | 'rtl';
  t: TranslationData;
  switchLanguage: (targetLang: 'en' | 'ar') => void;
  toggleLanguage: () => void;
}

interface LanguageProviderProps {
  children: ReactNode;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const location = useLocation();

  const isArabic = location.pathname === '/ar' || location.pathname.startsWith('/ar/');
  const lang: 'en' | 'ar' = isArabic ? 'ar' : 'en';
  const t = translations[lang];
  const dir = t.dir;

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    document.title = t.title;
  }, [lang, dir, t.title]);

  const switchLanguage = (targetLang: 'en' | 'ar') => {
    if (targetLang === 'ar') {
      window.location.href = '/ar';
    } else {
      window.location.href = '/';
    }
  };

  const toggleLanguage = () => {
    if (lang === 'en') {
      window.location.href = '/ar';
    } else {
      window.location.href = '/';
    }
  };

  return (
    <LanguageContext.Provider value={{ lang, dir, t, switchLanguage, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
